import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ModuleSummary } from '../services/terraform-api';

interface TerraformModulePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (moduleName: string) => void;
  modules: ModuleSummary[];
}

export const TerraformModulePicker: React.FC<TerraformModulePickerProps> = ({
  open,
  onClose,
  onSelect,
  modules,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return modules;
    const lower = search.toLowerCase();
    return modules.filter(m => m.name.toLowerCase().includes(lower));
  }, [modules, search]);

  const handleSelect = (name: string) => {
    onSelect(name);
    onClose();
    setSearch('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Module</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search modules..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {filtered.length} module{filtered.length !== 1 ? 's' : ''}
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          <List dense disablePadding>
            {filtered.map(mod => (
              <ListItem key={mod.name} disablePadding>
                <ListItemButton onClick={() => handleSelect(mod.name)}>
                  <ListItemText
                    primary={mod.name}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={`${mod.variableCount} var${mod.variableCount !== 1 ? 's' : ''}`}
                          variant="outlined"
                        />
                        {mod.requiredCount > 0 && (
                          <Chip
                            size="small"
                            label={`${mod.requiredCount} required`}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{ fontFamily: 'monospace' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              {modules.length === 0
                ? 'No modules loaded. Configure the module registry first.'
                : 'No matching modules'}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
